import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { useApproverSearch } from 'hooks/api/das/useApprovers';
import type { ApproverDto } from 'api/types/das/approvers.types';
import type {
  RequestType,
  RequestPriority,
  ID,
  EnhancedAttachment,
} from 'api/types';

/**
 * ============================================================================
 * CONSOLIDATED REQUEST MODAL - DIGITAL APPROVAL SYSTEM
 * ============================================================================
 *
 * Purpose: Allow pre-approvers to consolidate multiple pre-approved tickets
 *          of the same request type into a single request for final approval
 *
 * Features:
 * ✅ Display pre-approved tickets available for consolidation
 * ✅ Filter tickets by request type
 * ✅ Multi-select tickets with checkbox selection
 * ✅ Show ticket creator, value, and pre-approver for each ticket
 * ✅ Calculate and display total combined value
 * ✅ Select final approver (handles cases with different final approvers)
 * ✅ Attach additional documents to consolidated request
 * ✅ Tabbed interface similar to NewTicketModal
 * ✅ Comprehensive validation
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents a pre-approved ticket that can be consolidated
 */
export interface PreApprovedTicket {
  id: ID;
  ticketNo: string;
  subject: string;
  description?: string;
  requestType: RequestType;
  value: number;
  priority: RequestPriority;
  
  // Creator Information
  createdBy: {
    id: ID;
    name: string;
    department?: string;
  };
  createdDate: string;
  
  // Pre-Approval Information
  preApprovedBy: {
    id: ID;
    name: string;
    role?: string;
  };
  preApprovedDate: string;
  preApprovalComment?: string;
  
  // Original Final Approver
  finalApprover: {
    id: ID;
    name: string;
    role?: string;
    type: 'user' | 'role';
  };
  
  // Attachments count from original ticket
  attachmentCount: number;
}

/**
 * Final approver option for selection
 */
export interface FinalApproverOption {
  id: ID;
  name: string;
  role?: string;
  department?: string;
  type: 'user' | 'role';
  ticketCount: number; // Number of selected tickets with this approver
}

/**
 * Form data for consolidated request
 */
export interface ConsolidatedRequestFormData {
  title: string;
  description: string;
  requestType: RequestType | '';
  priority: RequestPriority;
  selectedTicketIds: ID[];
  finalApproverId: ID | null;
  finalApproverType?: 'user' | 'role'; // Type of the selected approver
  finalApproverName?: string; // Display name of the selected approver
  approverChangeReason?: string; // Reason when selecting a different approver
  isApproverChanged?: boolean; // Whether the approver was changed from original
  dueDate?: string;
  attachments: EnhancedAttachment[];
}

export interface ConsolidatedRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: ConsolidatedRequestFormData, selectedTickets: PreApprovedTicket[]) => void;
  preApprovedTickets: PreApprovedTicket[];
  currentUserId: ID;
  currentUserName: string;
}

interface FormErrors {
  title?: string;
  description?: string;
  selectedTickets?: string;
  finalApprover?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const REQUEST_TYPES: RequestType[] = [
  'Purchase Request',
  'Leave Request',
  'Travel Request',
  'Budget Approval',
  'Policy Exception',
  'General Request',
];

const PRIORITIES: RequestPriority[] = ['Normal', 'Medium', 'High', 'Urgent'];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatValue = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const getPriorityColor = (priority: RequestPriority): string => {
  switch (priority) {
    case 'Urgent':
      return '#ef4444';
    case 'High':
      return '#f59e0b';
    case 'Medium':
      return '#3b82f6';
    case 'Normal':
    default:
      return '#10b981';
  }
};

// ============================================================================
// TICKET SELECTION TABLE COMPONENT
// ============================================================================

interface TicketSelectionTableProps {
  tickets: PreApprovedTicket[];
  selectedIds: ID[];
  onSelectionChange: (ids: ID[]) => void;
  requestTypeFilter: RequestType | '';
}

const TicketSelectionTable: React.FC<TicketSelectionTableProps> = ({
  tickets,
  selectedIds,
  onSelectionChange,
  requestTypeFilter,
}) => {
  const [sortColumn, setSortColumn] = useState<keyof PreApprovedTicket>('createdDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedTicketId, setExpandedTicketId] = useState<ID | null>(null);

  // Filter tickets by request type
  const filteredTickets = useMemo(() => {
    if (!requestTypeFilter) return tickets;
    return tickets.filter((t) => t.requestType === requestTypeFilter);
  }, [tickets, requestTypeFilter]);

  // Sort tickets
  const sortedTickets = useMemo(() => {
    return [...filteredTickets].sort((a, b) => {
      let aVal: any = a[sortColumn];
      let bVal: any = b[sortColumn];

      // Handle nested objects
      if (sortColumn === 'createdDate' || sortColumn === 'preApprovedDate') {
        aVal = new Date(aVal as string).getTime();
        bVal = new Date(bVal as string).getTime();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredTickets, sortColumn, sortDirection]);

  const handleSort = (column: keyof PreApprovedTicket) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleToggleSelect = (id: ID) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === sortedTickets.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(sortedTickets.map((t) => t.id));
    }
  };

  const toggleExpand = (id: ID) => {
    setExpandedTicketId(expandedTicketId === id ? null : id);
  };

  return (
    <div className="ticket-selection-container">
      <table className="ticket-selection-table">
        <thead>
          <tr>
            <th style={{ width: '40px' }}>
              <input
                type="checkbox"
                checked={selectedIds.length === sortedTickets.length && sortedTickets.length > 0}
                onChange={handleSelectAll}
                title="Select all"
              />
            </th>
            <th style={{ width: '30px' }}></th>
            <th
              onClick={() => handleSort('ticketNo')}
              style={{ cursor: 'pointer', minWidth: '120px' }}
            >
              Ticket No {sortColumn === 'ticketNo' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('subject')} style={{ cursor: 'pointer' }}>
              Subject {sortColumn === 'subject' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ minWidth: '120px' }}>Creator</th>
            <th style={{ minWidth: '120px' }}>Pre-Approved By</th>
            <th
              onClick={() => handleSort('value')}
              style={{ cursor: 'pointer', minWidth: '100px', textAlign: 'right' }}
            >
              Value {sortColumn === 'value' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ minWidth: '120px' }}>Final Approver</th>
            <th style={{ minWidth: '80px', textAlign: 'center' }}>Priority</th>
          </tr>
        </thead>
        <tbody>
          {!requestTypeFilter ? (
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  <IconSystem.filter size={24} style={{ opacity: 0.5 }} />
                  <span>Please select a Request Type to view available requests</span>
                </div>
              </td>
            </tr>
          ) : sortedTickets.length === 0 ? (
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                No pre-approved requests found for "{requestTypeFilter}"
              </td>
            </tr>
          ) : (
            sortedTickets.map((ticket) => (
              <React.Fragment key={ticket.id}>
                <tr
                  className={`ticket-row ${selectedIds.includes(ticket.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleSelect(ticket.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(ticket.id)}
                      onChange={() => handleToggleSelect(ticket.id)}
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="expand-btn"
                      onClick={() => toggleExpand(ticket.id)}
                      title={expandedTicketId === ticket.id ? 'Collapse' : 'Expand'}
                    >
                      {expandedTicketId === ticket.id ? (
                        <IconSystem.chevronUp size={14} />
                      ) : (
                        <IconSystem.chevronDown size={14} />
                      )}
                    </button>
                  </td>
                  <td>
                    <span className="ticket-no">{ticket.ticketNo}</span>
                  </td>
                  <td>
                    <div className="ticket-subject">{ticket.subject}</div>
                    <div className="ticket-type-badge">{ticket.requestType}</div>
                  </td>
                  <td>
                    <div className="person-info">
                      <span className="person-name">{ticket.createdBy.name}</span>
                      <span className="person-detail">{ticket.createdBy.department}</span>
                    </div>
                  </td>
                  <td>
                    <div className="person-info">
                      <span className="person-name">{ticket.preApprovedBy.name}</span>
                      <span className="person-detail">{ticket.preApprovedBy.role}</span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="ticket-value">{formatValue(ticket.value)}</span>
                  </td>
                  <td>
                    <div className="person-info">
                      <span className="person-name">{ticket.finalApprover.name}</span>
                      <span className="person-detail">
                        {ticket.finalApprover.role}
                        {ticket.finalApprover.type === 'role' && (
                          <span className="role-badge">Role</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(ticket.priority) }}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                </tr>
                {/* Expanded Details Row */}
                {expandedTicketId === ticket.id && (
                  <tr className="expanded-row">
                    <td colSpan={9}>
                      <div className="expanded-content">
                        <div className="expanded-section">
                          <h4>Description</h4>
                          <p>{ticket.description || 'No description provided'}</p>
                        </div>
                        <div className="expanded-grid">
                          <div className="expanded-section">
                            <h4>Creation Details</h4>
                            <p>
                              <strong>Created:</strong> {formatDateTime(ticket.createdDate)}
                            </p>
                            <p>
                              <strong>By:</strong> {ticket.createdBy.name} ({ticket.createdBy.department})
                            </p>
                          </div>
                          <div className="expanded-section">
                            <h4>Pre-Approval Details</h4>
                            <p>
                              <strong>Pre-Approved:</strong> {formatDateTime(ticket.preApprovedDate)}
                            </p>
                            <p>
                              <strong>By:</strong> {ticket.preApprovedBy.name} ({ticket.preApprovedBy.role})
                            </p>
                            {ticket.preApprovalComment && (
                              <p>
                                <strong>Comment:</strong> {ticket.preApprovalComment}
                              </p>
                            )}
                          </div>
                          <div className="expanded-section">
                            <h4>Attachments</h4>
                            <p>
                              <IconSystem.file size={14} style={{ marginRight: '0.25rem' }} />
                              {ticket.attachmentCount} document(s) attached
                            </p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// SELECTED TICKETS SUMMARY COMPONENT
// ============================================================================

interface SelectedTicketsSummaryProps {
  selectedTickets: PreApprovedTicket[];
  onRemove: (id: ID) => void;
}

const SelectedTicketsSummary: React.FC<SelectedTicketsSummaryProps> = ({
  selectedTickets,
  onRemove,
}) => {
  const totalValue = useMemo(
    () => selectedTickets.reduce((sum, t) => sum + t.value, 0),
    [selectedTickets]
  );

  const totalAttachments = useMemo(
    () => selectedTickets.reduce((sum, t) => sum + t.attachmentCount, 0),
    [selectedTickets]
  );

  if (selectedTickets.length === 0) {
    return (
      <div className="summary-empty">
        <IconSystem.file size={24} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
        <p>No requests selected for consolidation</p>
        <p className="summary-hint">Select requests from the list above to consolidate</p>
      </div>
    );
  }

  return (
    <div className="selected-summary">
      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-item">
          <span className="stat-value">{selectedTickets.length}</span>
          <span className="stat-label">Requests</span>
        </div>
        <div className="stat-item highlight">
          <span className="stat-value">{formatValue(totalValue)}</span>
          <span className="stat-label">Total Value</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{totalAttachments}</span>
          <span className="stat-label">Attachments</span>
        </div>
      </div>

      {/* Selected Tickets List */}
      <div className="selected-tickets-list">
        {selectedTickets.map((ticket) => (
          <div key={ticket.id} className="selected-ticket-card">
            <div className="card-main">
              <div className="card-header">
                <span className="ticket-no">{ticket.ticketNo}</span>
                <span className="ticket-value">{formatValue(ticket.value)}</span>
              </div>
              <div className="card-subject">{ticket.subject}</div>
              <div className="card-details">
                <span>
                  <IconSystem.user size={12} /> {ticket.createdBy.name}
                </span>
                <span>
                  <IconSystem.check size={12} /> {ticket.preApprovedBy.name}
                </span>
              </div>
            </div>
            <button
              className="remove-btn"
              onClick={() => onRemove(ticket.id)}
              title="Remove from selection"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// FINAL APPROVER SELECTION COMPONENT
// ============================================================================

interface FinalApproverSelectionProps {
  selectedTickets: PreApprovedTicket[];
  selectedApproverId: ID | null;
  selectedApproverDetails: FinalApproverOption | null; // Persisted from parent
  approverChangeReason: string; // Reason for selecting different approver
  onSelect: (id: ID, approverDetails?: FinalApproverOption) => void;
  onReasonChange: (reason: string) => void; // Callback for reason changes
}

const FinalApproverSelection: React.FC<FinalApproverSelectionProps> = ({
  selectedTickets,
  selectedApproverId,
  selectedApproverDetails,
  approverChangeReason,
  onSelect,
  onReasonChange,
}) => {
  // State for "other approver" search
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showOtherApprover, setShowOtherApprover] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Use the approver search hook for API integration
  const { results: apiSearchResults, loading: searchLoading, search: searchApprovers } = useApproverSearch();

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchApprovers(searchQuery, undefined, 10);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchApprovers]);

  // Extract unique final approvers from selected tickets
  const approverOptions = useMemo(() => {
    const approverMap = new Map<ID, FinalApproverOption>();

    selectedTickets.forEach((ticket) => {
      const approver = ticket.finalApprover;
      if (approverMap.has(approver.id)) {
        const existing = approverMap.get(approver.id)!;
        existing.ticketCount += 1;
      } else {
        approverMap.set(approver.id, {
          id: approver.id,
          name: approver.name,
          role: approver.role,
          type: approver.type,
          ticketCount: 1,
        });
      }
    });

    return Array.from(approverMap.values()).sort((a, b) => b.ticketCount - a.ticketCount);
  }, [selectedTickets]);

  // Convert API results to FinalApproverOption format, excluding already listed approvers
  const searchResults = useMemo(() => {
    const existingIds = new Set(approverOptions.map(a => a.id));

    return apiSearchResults
      .filter((approver: ApproverDto) => !existingIds.has(approver.id))
      .map((approver: ApproverDto): FinalApproverOption => ({
        id: approver.id,
        name: approver.name,
        role: approver.role,
        department: approver.department,
        type: approver.type === 'Role' ? 'role' : 'user',
        ticketCount: 0,
      }))
      .slice(0, 5);
  }, [apiSearchResults, approverOptions]);

  // Check if selected approver is a custom one (not from ticket approvers)
  const isCustomApproverSelected = useMemo(() => {
    if (!selectedApproverId) return false;
    return !approverOptions.some(a => a.id === selectedApproverId);
  }, [selectedApproverId, approverOptions]);

  // Only use selectedApproverDetails as custom approver when it's actually a custom selection
  // (not one of the ticket-based approvers)
  const selectedCustomApprover = isCustomApproverSelected ? selectedApproverDetails : null;

  // Sync showOtherApprover state when component mounts with existing custom selection
  useEffect(() => {
    if (isCustomApproverSelected && selectedCustomApprover) {
      setShowOtherApprover(true);
    }
  }, [isCustomApproverSelected, selectedCustomApprover]);

  const hasDifferentApprovers = approverOptions.length > 1;

  // Handle selecting a custom approver from search
  const handleSelectCustomApprover = useCallback((approver: FinalApproverOption) => {
    // Parent will update selectedApproverDetails via onSelect callback
    onSelect(approver.id, approver);
    setSearchQuery('');
    setIsSearchFocused(false);
    setShowOtherApprover(true);
  }, [onSelect]);

  // Handle clicking the "Other" option
  const handleOtherOptionClick = useCallback(() => {
    setShowOtherApprover(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  // Handle selecting a ticket approver (non-custom)
  const handleSelectTicketApprover = useCallback((approver: FinalApproverOption) => {
    setShowOtherApprover(false);
    setSearchQuery('');
    // Parent will update selectedApproverDetails via onSelect callback
    onSelect(approver.id, approver);
  }, [onSelect]);

  if (selectedTickets.length === 0) {
    return (
      <div className="approver-selection-empty">
        <p>Select requests first to see final approver options</p>
      </div>
    );
  }

  return (
    <div className="approver-selection">
      {hasDifferentApprovers && (
        <div className="approver-warning">
          <IconSystem.warning size={16} />
          <span>
            Selected requests have different final approvers. Please choose one final approver for
            the consolidated request.
          </span>
        </div>
      )}

      <div className="approver-options">
        {/* Ticket Approvers */}
        {approverOptions.map((approver) => (
          <div
            key={approver.id}
            className={`approver-option ${selectedApproverId === approver.id && !isCustomApproverSelected ? 'selected' : ''}`}
            onClick={() => handleSelectTicketApprover(approver)}
          >
            <div className="approver-radio">
              <input
                type="radio"
                name="finalApprover"
                checked={selectedApproverId === approver.id && !isCustomApproverSelected}
                onChange={() => handleSelectTicketApprover(approver)}
              />
            </div>
            <div className="approver-info">
              <div className="approver-name">
                {approver.name}
                {approver.type === 'role' && <span className="role-badge">Role</span>}
              </div>
              <div className="approver-role">{approver.role}</div>
            </div>
            <div className="approver-ticket-count">
              <span className="count">{approver.ticketCount}</span>
              <span className="label">request{approver.ticketCount !== 1 ? 's' : ''}</span>
            </div>
            {approver.ticketCount === selectedTickets.length && (
              <div className="recommended-badge">Recommended</div>
            )}
          </div>
        ))}

        {/* Divider */}
        <div className="approver-divider">
          <span>or</span>
        </div>

        {/* Other Approver Option - with integrated search */}
        <div
          className={`approver-option other-approver-option ${showOtherApprover || isCustomApproverSelected ? 'selected expanded' : ''}`}
        >
          {/* Header row - always visible */}
          <div
            className="other-approver-header"
            onClick={!showOtherApprover && !isCustomApproverSelected ? handleOtherOptionClick : undefined}
          >
            <div className="approver-radio">
              <input
                type="radio"
                name="finalApprover"
                checked={showOtherApprover || isCustomApproverSelected}
                onChange={handleOtherOptionClick}
              />
            </div>
            <div className="approver-info">
              <div className="approver-name">
                <IconSystem.search size={14} style={{ marginRight: '0.5rem', opacity: 0.6 }} />
                Select a different approver
              </div>
              {!showOtherApprover && !isCustomApproverSelected && (
                <div className="approver-role">Search for another approver by name or role</div>
              )}
            </div>
          </div>

          {/* Integrated Search Section - inside the same box */}
          {(showOtherApprover || isCustomApproverSelected) && (
            <div className="approver-search-container integrated">
              {/* Show selected custom approver */}
              {selectedCustomApprover && !isSearchFocused && (
                <>
                  <div className="selected-custom-approver">
                    <div className="selected-approver-info">
                      {selectedCustomApprover.type === 'role' ? (
                        <IconSystem.users size={16} />
                      ) : (
                        <IconSystem.user size={16} />
                      )}
                      <div className="selected-approver-details">
                        <span className="selected-approver-name">
                          {selectedCustomApprover.name}
                          {selectedCustomApprover.type === 'role' && (
                            <span className="role-badge" style={{ marginLeft: '0.5rem' }}>Role</span>
                          )}
                        </span>
                        <span className="selected-approver-role">
                          {selectedCustomApprover.role}
                          {selectedCustomApprover.department && ` • ${selectedCustomApprover.department}`}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="change-approver-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSearchFocused(true);
                        setTimeout(() => searchInputRef.current?.focus(), 100);
                      }}
                    >
                      Change
                    </button>
                  </div>

                  {/* Reason for selecting different approver */}
                  <div className="approver-change-reason">
                    <label className="reason-label">
                      <IconSystem.edit size={14} />
                      Reason for selecting a different approver
                      <span className="optional-text">(recommended)</span>
                    </label>
                    <textarea
                      className="reason-textarea"
                      placeholder="e.g., Higher approval authority required for combined value, Original approver unavailable, etc."
                      value={approverChangeReason}
                      onChange={(e) => onReasonChange(e.target.value)}
                      rows={2}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </>
              )}

              {/* Search Input */}
              {(!selectedCustomApprover || isSearchFocused) && (
                <div className="approver-search-input-wrapper">
                  <IconSystem.search size={16} className="search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="approver-search-input"
                    placeholder="Search by name, role, or department..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                      // Delay to allow click on results
                      setTimeout(() => setIsSearchFocused(false), 300);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSearchQuery('');
                      }}
                    >
                      <IconSystem.x size={14} />
                    </button>
                  )}
                </div>
              )}

              {/* Search Results Dropdown */}
              {isSearchFocused && searchQuery.length >= 2 && (
                <div className="approver-search-results">
                  {searchLoading ? (
                    <div className="search-loading">
                      <span>Searching...</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((approver) => (
                      <div
                        key={approver.id}
                        className="search-result-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectCustomApprover(approver);
                        }}
                      >
                        <div className="result-avatar">
                          {approver.type === 'role' ? (
                            <IconSystem.users size={16} />
                          ) : (
                            <IconSystem.user size={16} />
                          )}
                        </div>
                        <div className="result-info">
                          <div className="result-name">
                            {approver.name}
                            {approver.type === 'role' && <span className="role-badge">Role</span>}
                          </div>
                          <div className="result-details">
                            {approver.role}
                            {approver.department && ` • ${approver.department}`}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">
                      <IconSystem.search size={20} style={{ opacity: 0.4 }} />
                      <p>No approvers found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              )}

              {/* Hint for minimum characters */}
              {isSearchFocused && searchQuery.length > 0 && searchQuery.length < 2 && (
                <div className="search-hint">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

export const ConsolidatedRequestModal: React.FC<ConsolidatedRequestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  preApprovedTickets = [],
  currentUserId,
  currentUserName,
}) => {
  // Form state
  const [formData, setFormData] = useState<ConsolidatedRequestFormData>({
    title: '',
    description: '',
    requestType: '',
    priority: 'Normal',
    selectedTicketIds: [],
    finalApproverId: null,
    dueDate: undefined,
    attachments: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'tickets' | 'details' | 'attachments' | 'review'>(
    'tickets'
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    type: 'success' | 'error';
    message: string;
    requestNumber?: string;
  } | null>(null);

  // Attachment state
  const [newAttachment, setNewAttachment] = useState<{
    file: File | null;
    title: string;
    description: string;
  }>({
    file: null,
    title: '',
    description: '',
  });

  // Preview attachment state
  const [previewAttachment, setPreviewAttachment] = useState<{
    url: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  // Selected approver details (for displaying in Review tab, especially for custom approvers)
  const [selectedApproverDetails, setSelectedApproverDetails] = useState<FinalApproverOption | null>(null);

  // Get selected tickets
  const selectedTickets = useMemo(
    () => preApprovedTickets.filter((t) => formData.selectedTicketIds.includes(t.id)),
    [preApprovedTickets, formData.selectedTicketIds]
  );

  // Debug logging for troubleshooting
  useEffect(() => {
    console.log('📋 ConsolidatedRequestModal Debug:', {
      preApprovedTicketsCount: preApprovedTickets.length,
      selectedTicketIdsCount: formData.selectedTicketIds.length,
      selectedTicketsCount: selectedTickets.length,
      preApprovedTickets: preApprovedTickets.map(t => ({ id: t.id, ticketNo: t.ticketNo })),
      selectedTicketIds: formData.selectedTicketIds,
    });
  }, [preApprovedTickets, formData.selectedTicketIds, selectedTickets]);

  // Calculate totals
  const totalValue = useMemo(
    () => selectedTickets.reduce((sum, t) => sum + t.value, 0),
    [selectedTickets]
  );

  // Get unique original approvers from selected tickets (for Review tab summary)
  const originalApprovers = useMemo(() => {
    const approverMap = new Map<ID, FinalApproverOption>();
    selectedTickets.forEach((ticket) => {
      const approver = ticket.finalApprover;
      if (approverMap.has(approver.id)) {
        const existing = approverMap.get(approver.id)!;
        existing.ticketCount += 1;
      } else {
        approverMap.set(approver.id, {
          id: approver.id,
          name: approver.name,
          role: approver.role,
          type: approver.type,
          ticketCount: 1,
        });
      }
    });
    return Array.from(approverMap.values());
  }, [selectedTickets]);

  // Check if the selected approver is different from the original ticket approvers
  const isApproverChanged = useMemo(() => {
    if (!formData.finalApproverId) return false;
    return !originalApprovers.some(a => a.id === formData.finalApproverId);
  }, [formData.finalApproverId, originalApprovers]);

  // Get available request types from selected tickets
  const availableRequestTypes = useMemo(() => {
    const types = new Set(preApprovedTickets.map((t) => t.requestType));
    return Array.from(types);
  }, [preApprovedTickets]);

  // Filter tickets by selected request type
  const filteredTickets = useMemo(() => {
    if (!formData.requestType) return preApprovedTickets;
    return preApprovedTickets.filter((t) => t.requestType === formData.requestType);
  }, [preApprovedTickets, formData.requestType]);

  // Auto-select final approver ONLY when no approver has been selected yet
  // This prevents overriding user's custom approver selection when switching tabs
  useEffect(() => {
    // Only auto-select if no approver is currently selected
    if (selectedTickets.length > 0 && formData.finalApproverId === null) {
      const approverIds = new Set(selectedTickets.map((t) => t.finalApprover.id));
      if (approverIds.size === 1) {
        const singleApprover = selectedTickets[0].finalApprover;
        setFormData((prev) => ({
          ...prev,
          finalApproverId: singleApprover.id,
          finalApproverType: singleApprover.type,
          finalApproverName: singleApprover.name, // Store name for API
          isApproverChanged: false, // Original approver from ticket
        }));
        // Also set the approver details for display in Review tab
        setSelectedApproverDetails({
          id: singleApprover.id,
          name: singleApprover.name,
          role: singleApprover.role,
          type: singleApprover.type,
          ticketCount: selectedTickets.length,
        });
      }
    }
  }, [selectedTickets, formData.finalApproverId]);

  // Auto-generate title based on selection
  // Re-generate when selection or totalValue changes, but only if title is empty or was auto-generated
  useEffect(() => {
    if (selectedTickets.length > 0) {
      const requestType = selectedTickets[0].requestType;
      const count = selectedTickets.length;
      const generatedTitle = `Consolidated ${requestType} (${count} items) - ${formatValue(totalValue)}`;

      // Update title if:
      // 1. Title is empty, OR
      // 2. Title matches the auto-generated pattern (user hasn't manually edited it)
      const isAutoGeneratedPattern = /^Consolidated .+ \(\d+ items\) - [\d,]+\.\d{2}$/.test(formData.title);

      if (!formData.title || isAutoGeneratedPattern) {
        setFormData((prev) => ({ ...prev, title: generatedTitle }));
      }
    }
  }, [selectedTickets, totalValue]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (formData.selectedTicketIds.length === 0) {
      newErrors.selectedTickets = 'Please select at least 1 ticket to consolidate';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 10) {
      newErrors.title = 'Title must be at least 10 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Justification/description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    } else if (formData.description.length > 10000) {
      newErrors.description = 'Description cannot exceed 10000 characters';
    }

    if (!formData.finalApproverId) {
      newErrors.finalApprover = 'Please select a final approver';
    }

    setErrors(newErrors);

    // Switch to tab with errors
    if (newErrors.selectedTickets) {
      setActiveTab('tickets');
    } else if (newErrors.title || newErrors.description) {
      setActiveTab('details');
    } else if (newErrors.finalApprover) {
      setActiveTab('details');
    }

    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleTicketSelectionChange = useCallback((ids: ID[]) => {
    setFormData((prev) => ({ ...prev, selectedTicketIds: ids }));
    setErrors((prev) => ({ ...prev, selectedTickets: undefined }));
  }, []);

  const handleRemoveTicket = useCallback((id: ID) => {
    setFormData((prev) => ({
      ...prev,
      selectedTicketIds: prev.selectedTicketIds.filter((i) => i !== id),
    }));
  }, []);

  const handleInputChange = useCallback(
    (field: keyof ConsolidatedRequestFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const handleRequestTypeChange = useCallback((type: RequestType | '') => {
    setFormData((prev) => ({
      ...prev,
      requestType: type,
      selectedTicketIds: [], // Clear selection when type changes
      finalApproverId: null,
      title: '', // Reset auto-generated title
    }));
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewAttachment((prev) => ({ ...prev, file }));
    }
  }, []);

  const handleAddAttachment = useCallback(() => {
    if (!newAttachment.file) {
      alert('Please select a file');
      return;
    }
    if (!newAttachment.title.trim()) {
      alert('Please provide a title for the attachment');
      return;
    }

    const enhancedAttachment: EnhancedAttachment = {
      file: newAttachment.file,
      title: newAttachment.title,
      description: newAttachment.description,
    };

    setFormData((prev) => ({
      ...prev,
      attachments: [...(prev.attachments || []), enhancedAttachment],
    }));

    setNewAttachment({ file: null, title: '', description: '' });
    const fileInput = document.getElementById('consolidated-file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }, [newAttachment]);

  const handleRemoveAttachment = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  }, []);

  const handlePreviewAttachment = useCallback((attachment: EnhancedAttachment) => {
    // Create object URL for the file
    const url = URL.createObjectURL(attachment.file);
    setPreviewAttachment({
      url,
      fileName: attachment.title || attachment.file.name,
      fileType: attachment.file.type,
    });
  }, []);

  const handleClosePreview = useCallback(() => {
    if (previewAttachment?.url) {
      URL.revokeObjectURL(previewAttachment.url);
    }
    setPreviewAttachment(null);
  }, [previewAttachment]);

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      if (onSubmit) {
        await onSubmit(formData, selectedTickets);
      }
      // Show success message
      setSubmitResult({
        type: 'success',
        message: `Consolidated request created successfully with ${selectedTickets.length} item(s). The request has been submitted for final approval.`,
      });
    } catch (error) {
      console.error('Error submitting consolidated request:', error);
      setSubmitResult({
        type: 'error',
        message: 'Failed to submit consolidated request. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  const handleCloseResultAlert = () => {
    setSubmitResult(null);
    if (submitResult?.type === 'success') {
      handleClose();
    }
  };

  const handleClose = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      requestType: '',
      priority: 'Normal',
      selectedTicketIds: [],
      finalApproverId: null,
      dueDate: undefined,
      attachments: [],
    });
    setErrors({});
    setNewAttachment({ file: null, title: '', description: '' });
    setActiveTab('tickets');
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">Create Consolidated Request</h2>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: 0 }}>
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button
                type="button"
                className={`tab-button ${activeTab === 'tickets' ? 'active' : ''} ${errors.selectedTickets ? 'has-error' : ''}`}
                onClick={() => setActiveTab('tickets')}
              >
                <IconSystem.file size={16} />
                <span>Select Requests</span>
                {errors.selectedTickets && <span className="error-indicator">!</span>}
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'details' ? 'active' : ''} ${errors.title || errors.description || errors.finalApprover ? 'has-error' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <IconSystem.edit size={16} />
                <span>Request Details</span>
                {(errors.title || errors.description || errors.finalApprover) && (
                  <span className="error-indicator">!</span>
                )}
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'attachments' ? 'active' : ''}`}
                onClick={() => setActiveTab('attachments')}
              >
                <IconSystem.upload size={16} />
                <span>Attachments</span>
                <span className="optional-label">(Optional)</span>
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
                onClick={() => setActiveTab('review')}
              >
                <IconSystem.eye size={16} />
                <span>Review & Submit</span>
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Tab 1: Select Tickets */}
              {activeTab === 'tickets' && (
                <div className="tab-panel">
                  {/* Request Type Filter */}
                  <div className="filter-bar">
                    <div className="filter-group">
                      <label className="filter-label">Filter by Request Type:</label>
                      <select
                        className="form-select"
                        value={formData.requestType}
                        onChange={(e) => handleRequestTypeChange(e.target.value as RequestType | '')}
                        style={{ minWidth: '200px' }}
                      >
                        <option value="">All Request Types</option>
                        {availableRequestTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-info">
                      <span>{filteredTickets.length} request(s) available</span>
                    </div>
                  </div>

                  {/* Ticket Selection Table */}
                  <div className="section-title">
                    <h3>Pre-Approved Requests ({filteredTickets.length} available)</h3>
                    <p>Select requests to consolidate into a single request</p>
                  </div>

                  {filteredTickets.length === 0 && (
                    <div
                      className="info-message"
                      style={{
                        padding: '1rem',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '0.375rem',
                        marginBottom: '1rem',
                        color: '#92400e',
                      }}
                    >
                      <strong>Note:</strong> There are no pre-approved requests available for consolidation.
                    </div>
                  )}

                  <TicketSelectionTable
                    tickets={filteredTickets}
                    selectedIds={formData.selectedTicketIds}
                    onSelectionChange={handleTicketSelectionChange}
                    requestTypeFilter={formData.requestType}
                  />

                  {errors.selectedTickets && (
                    <div className="form-error" style={{ marginTop: '0.5rem' }}>
                      {errors.selectedTickets}
                    </div>
                  )}

                  {/* Selected Tickets Summary */}
                  <div className="section-title" style={{ marginTop: '1.5rem' }}>
                    <h3>Selected for Consolidation</h3>
                    <p>Review selected requests and total value</p>
                  </div>

                  <SelectedTicketsSummary
                    selectedTickets={selectedTickets}
                    onRemove={handleRemoveTicket}
                  />
                </div>
              )}

              {/* Tab 2: Request Details */}
              {activeTab === 'details' && (
                <div className="tab-panel">
                  {/* Final Approver Selection */}
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.user size={18} />
                      Final Approver
                      <span style={{ color: '#ef4444' }}> *</span>
                    </h3>

                    <FinalApproverSelection
                      selectedTickets={selectedTickets}
                      selectedApproverId={formData.finalApproverId}
                      selectedApproverDetails={selectedApproverDetails}
                      approverChangeReason={formData.approverChangeReason || ''}
                      onSelect={(id, approverDetails) => {
                        handleInputChange('finalApproverId', id);
                        if (approverDetails) {
                          setSelectedApproverDetails(approverDetails);
                          // Update approver type and name in form data
                          handleInputChange('finalApproverType', approverDetails.type);
                          handleInputChange('finalApproverName', approverDetails.name);
                          // Check if this is a custom approver (not from original tickets)
                          const isCustom = !originalApprovers.some(a => a.id === id);
                          handleInputChange('isApproverChanged', isCustom);
                        }
                      }}
                      onReasonChange={(reason) => handleInputChange('approverChangeReason', reason)}
                    />

                    {errors.finalApprover && (
                      <div className="form-error">{errors.finalApprover}</div>
                    )}
                  </div>

                  {/* Request Information */}
                  <div className="form-section" style={{ marginTop: '1.5rem' }}>
                    <h3 className="form-section-title">
                      <IconSystem.file size={18} />
                      Consolidated Request Information
                    </h3>

                    {/* Title */}
                    <div className="form-group">
                      <label className="form-label">
                        Title <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Enter consolidated request title"
                        disabled={isSubmitting}
                        style={{ border: errors.title ? '1px solid #ef4444' : '1px solid #334155' }}
                      />
                      {errors.title && <div className="form-error">{errors.title}</div>}
                    </div>

                    {/* Description/Justification */}
                    <div className="form-group">
                      <label className="form-label">
                        Justification / Description <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea
                        className="form-textarea"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Provide justification for consolidating these requests..."
                        rows={4}
                        maxLength={10000}
                        disabled={isSubmitting}
                        style={{
                          border: errors.description ? '1px solid #ef4444' : '1px solid #334155',
                          textAlign: 'justify'
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '4px'
                      }}>
                        {errors.description && <div className="form-error" style={{ margin: 0 }}>{errors.description}</div>}
                        <div style={{
                          marginLeft: 'auto',
                          fontSize: '12px',
                          color: formData.description.length > 9000 ? '#ef4444' : '#64748b'
                        }}>
                          {formData.description.length} / 10000
                        </div>
                      </div>
                    </div>

                    {/* Priority and Due Date */}
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Priority</label>
                        <select
                          className="form-select"
                          value={formData.priority}
                          onChange={(e) =>
                            handleInputChange('priority', e.target.value as RequestPriority)
                          }
                          disabled={isSubmitting}
                          style={{ border: '1px solid #334155' }}
                        >
                          {PRIORITIES.map((priority) => (
                            <option key={priority} value={priority}>
                              {priority}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Due Date (Optional)</label>
                        <input
                          type="date"
                          className="form-input"
                          value={formData.dueDate || ''}
                          onChange={(e) => handleInputChange('dueDate', e.target.value || undefined)}
                          disabled={isSubmitting}
                          style={{ border: '1px solid #334155' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 3: Additional Documents */}
              {activeTab === 'attachments' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <h3 className="form-section-title">
                      <IconSystem.upload size={18} />
                      Additional Documents
                      <span className="optional-label">(Optional)</span>
                    </h3>
                    <p className="section-description">
                      Attach additional supporting documents to the consolidated request. Documents
                      from individual tickets will be automatically included.
                    </p>

                    {/* Existing Attachments from Tickets */}
                    <div className="existing-attachments">
                      <h4>Documents from Selected Requests</h4>
                      {selectedTickets.length === 0 ? (
                        <p className="no-items">No requests selected</p>
                      ) : selectedTickets.filter(t => t.attachmentCount > 0).length === 0 ? (
                        <p className="no-items">No documents attached to selected requests</p>
                      ) : (
                        <div className="attachment-summary">
                          {selectedTickets
                            .filter((ticket) => ticket.attachmentCount > 0)
                            .map((ticket) => (
                              <div key={ticket.id} className="ticket-attachments">
                                <span className="ticket-ref">{ticket.ticketNo}</span>
                                <span className="attachment-count">
                                  <IconSystem.file size={14} />
                                  {ticket.attachmentCount} document(s)
                                </span>
                              </div>
                            ))}
                          <div className="total-attachments">
                            <strong>Total from requests:</strong>{' '}
                            {selectedTickets.reduce((sum, t) => sum + t.attachmentCount, 0)}{' '}
                            document(s)
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Add New Attachment */}
                    <div className="new-attachment-section">
                      <h4>Add New Documents</h4>
                      <div className="attachment-form-grid">
                        <div style={{ gridColumn: 'span 2' }}>
                          <input
                            id="consolidated-file-upload"
                            type="file"
                            onChange={handleFileSelect}
                            disabled={isSubmitting}
                            style={{ fontSize: '0.875rem' }}
                          />
                        </div>
                        <div style={{ gridColumn: 'span 3' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Document Title *"
                            value={newAttachment.title}
                            onChange={(e) =>
                              setNewAttachment((prev) => ({ ...prev, title: e.target.value }))
                            }
                            disabled={isSubmitting}
                            style={{ border: '1px solid #334155', fontSize: '0.875rem' }}
                          />
                        </div>
                        <div style={{ gridColumn: 'span 3' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Description (Optional)"
                            value={newAttachment.description}
                            onChange={(e) =>
                              setNewAttachment((prev) => ({ ...prev, description: e.target.value }))
                            }
                            disabled={isSubmitting}
                            style={{ border: '1px solid #334155', fontSize: '0.875rem' }}
                          />
                        </div>
                        <div style={{ gridColumn: 'span 1' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleAddAttachment}
                            disabled={isSubmitting || !newAttachment.file}
                            style={{ width: '100%', padding: '0.5rem' }}
                          >
                            <IconSystem.add size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Added Attachments List */}
                      {formData.attachments.length > 0 ? (
                        <div className="attachments-list">
                          {formData.attachments.map((attachment, index) => (
                            <div key={index} className="attachment-item">
                              <div className="attachment-info">
                                <div className="attachment-header">
                                  <IconSystem.file size={14} />
                                  <span className="attachment-title">{attachment.title}</span>
                                </div>
                                <div className="attachment-details">
                                  {attachment.file.name} ({formatFileSize(attachment.file.size)})
                                  {attachment.description && ` • ${attachment.description}`}
                                </div>
                              </div>
                              <div className="attachment-actions">
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => handlePreviewAttachment(attachment)}
                                  disabled={isSubmitting}
                                  title="Preview attachment"
                                >
                                  <IconSystem.eye size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-danger"
                                  onClick={() => handleRemoveAttachment(index)}
                                  disabled={isSubmitting}
                                  title="Remove attachment"
                                >
                                  <IconSystem.trash size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-attachments">
                          No additional documents added
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 4: Review & Submit */}
              {activeTab === 'review' && (
                <div className="tab-panel">
                  <div className="review-section">
                    {/* Consolidated Request Summary */}
                    <div className="review-card">
                      <h3 className="review-card-title">
                        <IconSystem.file size={18} />
                        Consolidated Request Summary
                      </h3>
                      <div className="review-content">
                        <div className="review-row">
                          <span className="review-label">Title:</span>
                          <span className="review-value">{formData.title || '—'}</span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Request Type:</span>
                          <span className="review-value">
                            {formData.requestType || (selectedTickets.length > 0 ? selectedTickets[0].requestType : '—')}
                          </span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Priority:</span>
                          <span
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(formData.priority) }}
                          >
                            {formData.priority}
                          </span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Due Date:</span>
                          <span className="review-value">
                            {formData.dueDate ? formatDate(formData.dueDate) : 'Not specified'}
                          </span>
                        </div>
                        <div className="review-row full-width">
                          <span className="review-label">Justification:</span>
                          <span className="review-value description">
                            {formData.description || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Tickets Summary */}
                    <div className="review-card">
                      <h3 className="review-card-title">
                        <IconSystem.copy size={18} />
                        Consolidated Requests ({selectedTickets.length})
                      </h3>
                      <div className="review-tickets-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Request No</th>
                              <th>Subject</th>
                              <th>Creator</th>
                              <th>Pre-Approved By</th>
                              <th style={{ textAlign: 'right' }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedTickets.map((ticket) => (
                              <tr key={ticket.id}>
                                <td className="ticket-no">{ticket.ticketNo}</td>
                                <td>{ticket.subject}</td>
                                <td>{ticket.createdBy.name}</td>
                                <td>{ticket.preApprovedBy.name}</td>
                                <td style={{ textAlign: 'right' }}>
                                  {formatValue(ticket.value)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                                Total Value:
                              </td>
                              <td
                                style={{ textAlign: 'right', fontWeight: 700, color: '#3b82f6' }}
                              >
                                {formatValue(totalValue)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    {/* Final Approver & Consolidation Summary */}
                    <div className="review-card">
                      <h3 className="review-card-title">
                        <IconSystem.user size={18} />
                        Final Approver
                        {isApproverChanged && (
                          <span className="changed-badge">Changed</span>
                        )}
                      </h3>
                      <div className="review-content">
                        {formData.finalApproverId && selectedApproverDetails ? (
                          <>
                            <div className="approver-display">
                              <div className="approver-avatar">
                                {selectedApproverDetails.type === 'role' ? (
                                  <IconSystem.users size={24} />
                                ) : (
                                  <IconSystem.user size={24} />
                                )}
                              </div>
                              <div className="approver-details">
                                <span className="approver-name">
                                  {selectedApproverDetails.name}
                                  {selectedApproverDetails.type === 'role' && (
                                    <span className="role-badge" style={{ marginLeft: '0.5rem' }}>Role</span>
                                  )}
                                </span>
                                <span className="approver-role">
                                  {selectedApproverDetails.role}
                                  {selectedApproverDetails.department && ` • ${selectedApproverDetails.department}`}
                                </span>
                              </div>
                              <button
                                type="button"
                                className="change-approver-btn"
                                onClick={() => setActiveTab('details')}
                                style={{ marginLeft: 'auto' }}
                              >
                                Change
                              </button>
                            </div>

                            {/* Consolidation Summary - Show when approver was changed */}
                            {isApproverChanged && (
                              <div className="consolidation-summary">
                                <h4 className="summary-title">
                                  <IconSystem.info size={14} />
                                  Approver Change Summary
                                </h4>

                                {/* Original Approvers from Tickets */}
                                <div className="original-approvers">
                                  <span className="summary-label">Original Approver(s):</span>
                                  <div className="approver-list">
                                    {originalApprovers.map((approver) => (
                                      <div key={approver.id} className="original-approver-item">
                                        {approver.type === 'role' ? (
                                          <IconSystem.users size={12} />
                                        ) : (
                                          <IconSystem.user size={12} />
                                        )}
                                        <span>{approver.name}</span>
                                        <span className="ticket-count">
                                          ({approver.ticketCount} ticket{approver.ticketCount > 1 ? 's' : ''})
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Reason for Change */}
                                {formData.approverChangeReason && (
                                  <div className="change-reason">
                                    <span className="summary-label">Reason for Change:</span>
                                    <p className="reason-text">{formData.approverChangeReason}</p>
                                  </div>
                                )}

                                {!formData.approverChangeReason && (
                                  <div className="change-reason-warning">
                                    <IconSystem.alert size={14} />
                                    <span>No reason provided for approver change</span>
                                    <button
                                      type="button"
                                      className="add-reason-btn"
                                      onClick={() => setActiveTab('details')}
                                    >
                                      Add Reason
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="not-selected-container">
                            <span className="not-selected">No final approver selected</span>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setActiveTab('details')}
                              style={{ marginLeft: '1rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}
                            >
                              Select Approver
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Attachments Summary */}
                    <div className="review-card">
                      <h3 className="review-card-title">
                        <IconSystem.file size={18} />
                        Attachments
                      </h3>
                      <div className="review-content">
                        <div className="review-row">
                          <span className="review-label">From requests:</span>
                          <span className="review-value">
                            {selectedTickets.reduce((sum, t) => sum + t.attachmentCount, 0)}{' '}
                            document(s)
                          </span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Additional:</span>
                          <span className="review-value">
                            {formData.attachments.length} document(s)
                          </span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Total:</span>
                          <span className="review-value highlight">
                            {selectedTickets.reduce((sum, t) => sum + t.attachmentCount, 0) +
                              formData.attachments.length}{' '}
                            document(s)
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Consolidation Info */}
                    <div className="review-card info">
                      <h3 className="review-card-title">
                        <IconSystem.info size={18} />
                        Consolidation Information
                      </h3>
                      <div className="review-content">
                        <div className="review-row">
                          <span className="review-label">Consolidated By:</span>
                          <span className="review-value">{currentUserName}</span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Consolidation Date:</span>
                          <span className="review-value">{formatDateTime(new Date().toISOString())}</span>
                        </div>
                      </div>
                    </div>

                    {/* Validation Errors - only show when there are actual error messages */}
                    {(errors.selectedTickets || errors.title || errors.description || errors.finalApprover) && (
                      <div className="validation-errors">
                        <h4>
                          <IconSystem.warning size={16} />
                          Please fix the following errors before submitting:
                        </h4>
                        <ul>
                          {errors.selectedTickets && <li>{errors.selectedTickets}</li>}
                          {errors.title && <li>{errors.title}</li>}
                          {errors.description && <li>{errors.description}</li>}
                          {errors.finalApprover && <li>{errors.finalApprover}</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div className="footer-left">
            {selectedTickets.length > 0 && (
              <div className="footer-summary">
                <span className="summary-item">
                  <strong>{selectedTickets.length}</strong> requests
                </span>
                <span className="summary-divider">•</span>
                <span className="summary-item highlight">
                  <strong>{formatValue(totalValue)}</strong> total
                </span>
              </div>
            )}
          </div>
          <div className="footer-actions">
            <button className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            {activeTab !== 'review' ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const tabs: Array<'tickets' | 'details' | 'attachments' | 'review'> = [
                    'tickets',
                    'details',
                    'attachments',
                    'review',
                  ];
                  const currentIndex = tabs.indexOf(activeTab);
                  if (currentIndex < tabs.length - 1) {
                    setActiveTab(tabs[currentIndex + 1]);
                  }
                }}
              >
                Next
                <IconSystem.arrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  const isValid = validateForm();
                  if (!isValid) {
                    return;
                  }

                  // Show styled confirmation dialog
                  setShowConfirmDialog(true);
                }}
                disabled={isSubmitting || selectedTickets.length === 0}
              >
                {isSubmitting ? (
                  <>
                    <IconSystem.refresh size={16} className="spinner" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IconSystem.check size={16} />
                    Submit Consolidated Request
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog - Rendered via Portal to ensure proper z-index */}
      {showConfirmDialog && createPortal(
        <>
          <div className="confirm-dialog-backdrop" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10060,
          }} onClick={handleCancelConfirm} />
          <div className="confirm-dialog" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            zIndex: 10061,
            width: '90%',
            maxWidth: '500px',
            overflow: 'hidden',
          }}>
            <div className="confirm-dialog-header" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.25rem 1.5rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderBottom: '1px solid #f59e0b',
            }}>
              <IconSystem.warning size={24} style={{ color: '#f59e0b' }} />
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#92400e' }}>Confirm Submission</h3>
            </div>
            <div className="confirm-dialog-content" style={{ padding: '1.5rem' }}>
              <p style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>
                Are you sure you want to submit this consolidated request?
              </p>
              <div className="confirm-dialog-summary" style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: 500, color: '#64748b' }}>Title:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formData.title}</span>
                </div>
                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: 500, color: '#64748b' }}>Items to consolidate:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{selectedTickets.length} request(s)</span>
                </div>
                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ fontWeight: 500, color: '#64748b' }}>Total Value:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                    {formatValue(selectedTickets.reduce((sum, t) => sum + (t.value || 0), 0))}
                  </span>
                </div>
                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                  <span style={{ fontWeight: 500, color: '#64748b' }}>Final Approver:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>
                    {originalApprovers.find((a: FinalApproverOption) => a.id === formData.finalApproverId)?.name || 'Not selected'}
                  </span>
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic', margin: 0 }}>
                Once submitted, the consolidated request will be sent to the final approver for review.
              </p>
            </div>
            <div className="confirm-dialog-actions" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
            }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmSubmit}
              >
                <IconSystem.check size={16} />
                Confirm & Submit
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Result Alert - Rendered via Portal */}
      {submitResult && createPortal(
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10070,
          }} />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            zIndex: 10071,
            width: '90%',
            maxWidth: '400px',
            padding: '2rem',
            textAlign: 'center',
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: submitResult.type === 'success'
                ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              color: submitResult.type === 'success' ? '#059669' : '#dc2626',
            }}>
              {submitResult.type === 'success' ? (
                <IconSystem.check size={48} />
              ) : (
                <IconSystem.x size={48} />
              )}
            </div>
            <div>
              <h3 style={{
                margin: '0 0 0.5rem 0',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: submitResult.type === 'success' ? '#059669' : '#dc2626',
              }}>
                {submitResult.type === 'success' ? 'Success!' : 'Error'}
              </h3>
              <p style={{ margin: '0 0 1.5rem 0', color: '#64748b', fontSize: '1rem', lineHeight: 1.5 }}>
                {submitResult.message}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCloseResultAlert}
              style={{ minWidth: '120px' }}
            >
              {submitResult.type === 'success' ? 'Close' : 'Try Again'}
            </button>
          </div>
        </>,
        document.body
      )}

      <style>{`
        /* Component-specific styles for Consolidated Request Modal */

        /* Tab Navigation Styles */
        .tab-navigation {
          display: flex;
          gap: 0.5rem;
          padding: 0 1.5rem;
          border-bottom: 2px solid #e2e8f0;
          overflow-x: auto;
          position: sticky;
          top: 0;
          z-index: 20;
          background: #fff;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: transparent;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
          transition: all 0.2s;
          white-space: nowrap;
          position: relative;
        }

        .tab-button:hover {
          background: #f8fafc;
          color: #334155;
        }

        .tab-button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
          background: #eff6ff;
        }

        .tab-button.has-error {
          color: #ef4444;
        }

        .tab-button .error-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          background: #ef4444;
          color: white;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          margin-left: 0.25rem;
        }

        .tab-button .optional-label {
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 400;
        }

        .tab-content {
          padding: 1.5rem;
        }

        .tab-panel {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Ticket Selection Table */
        .ticket-selection-container {
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          overflow: hidden;
          max-height: 400px;
          overflow-y: auto;
        }

        .ticket-selection-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .ticket-selection-table thead {
          background: #f1f5f9;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .ticket-selection-table th {
          padding: 0.75rem;
          text-align: left;
          font-weight: 600;
          color: #475569;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .ticket-selection-table td {
          padding: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }

        .ticket-row {
          cursor: pointer;
          transition: background 0.15s;
        }

        .ticket-row:hover {
          background: #f8fafc;
        }

        .ticket-row.selected {
          background: #eff6ff;
        }

        .ticket-row.selected:hover {
          background: #dbeafe;
        }

        .expand-btn {
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 0.25rem;
          padding: 0.25rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .expand-btn:hover {
          background: #f1f5f9;
          color: #334155;
        }

        .ticket-no {
          font-weight: 600;
          color: #3b82f6;
        }

        .ticket-subject {
          font-weight: 500;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .ticket-type-badge {
          display: inline-block;
          font-size: 0.7rem;
          padding: 0.125rem 0.5rem;
          background: #e2e8f0;
          color: #475569;
          border-radius: 0.25rem;
        }

        .person-info {
          display: flex;
          flex-direction: column;
        }

        .person-name {
          font-weight: 500;
          color: #1e293b;
        }

        .person-detail {
          font-size: 0.75rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .role-badge {
          display: inline-block;
          font-size: 0.65rem;
          padding: 0.0625rem 0.375rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 0.25rem;
          margin-left: 0.25rem;
        }

        .ticket-value {
          font-weight: 600;
          color: #059669;
        }

        .priority-badge {
          display: inline-block;
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
          color: white;
          border-radius: 1rem;
          font-weight: 500;
        }

        /* Expanded Row */
        .expanded-row td {
          background: #f8fafc;
          padding: 0 !important;
        }

        .expanded-content {
          padding: 1rem 1.5rem;
          border-top: 2px solid #3b82f6;
        }

        .expanded-section {
          margin-bottom: 1rem;
        }

        .expanded-section:last-child {
          margin-bottom: 0;
        }

        .expanded-section h4 {
          font-size: 0.8rem;
          font-weight: 600;
          color: #64748b;
          margin: 0 0 0.5rem 0;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .expanded-section p {
          margin: 0.25rem 0;
          font-size: 0.875rem;
          color: #334155;
        }

        .expanded-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }

        /* Selected Tickets Summary */
        .summary-empty {
          text-align: center;
          padding: 2rem;
          background: #f8fafc;
          border-radius: 0.5rem;
          border: 2px dashed #cbd5e1;
          color: #64748b;
        }

        .summary-empty p {
          margin: 0;
        }

        .summary-hint {
          font-size: 0.8rem;
          margin-top: 0.5rem !important;
        }

        .selected-summary {
          background: #f8fafc;
          border-radius: 0.5rem;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .summary-stats {
          display: flex;
          justify-content: space-around;
          padding: 0.5rem;
          background: var(--bg-secondary, #f1f5f9);
          border-bottom: 1px solid #e2e8f0;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          display: block;
          font-size: 1rem;
          font-weight: 700;
          color: var(--primary, #3b82f6);
        }

        .stat-label {
          font-size: 0.7rem;
          color: #64748b;
        }

        .stat-item.highlight .stat-value {
          font-size: 1.125rem;
          color: #059669;
        }

        .selected-tickets-list {
          padding: 1rem;
          max-height: 200px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .selected-ticket-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: white;
          border-radius: 0.375rem;
          border: 1px solid #e2e8f0;
        }

        .card-main {
          flex: 1;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;
        }

        .card-subject {
          font-size: 0.875rem;
          color: #334155;
          margin-bottom: 0.25rem;
        }

        .card-details {
          display: flex;
          gap: 1rem;
          font-size: 0.75rem;
          color: #64748b;
        }

        .card-details span {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .remove-btn {
          background: transparent;
          border: 1px solid #e2e8f0;
          border-radius: 0.25rem;
          padding: 0.375rem;
          cursor: pointer;
          color: #64748b;
          transition: all 0.15s;
        }

        .remove-btn:hover {
          background: #fef2f2;
          border-color: #fecaca;
          color: #ef4444;
        }

        /* Approver Selection */
        .approver-selection-empty {
          padding: 1.5rem;
          text-align: center;
          background: #f8fafc;
          border-radius: 0.5rem;
          color: #64748b;
        }

        .approver-warning {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          color: #92400e;
          font-size: 0.875rem;
        }

        .approver-options {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .approver-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.15s;
        }

        .approver-option:hover {
          border-color: #3b82f6;
          background: #f8fafc;
        }

        .approver-option.selected {
          border-color: #3b82f6;
          background: #eff6ff;
        }

        .approver-radio input {
          width: 1.125rem;
          height: 1.125rem;
          accent-color: #3b82f6;
        }

        .approver-info {
          flex: 1;
        }

        .approver-name {
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .approver-role {
          font-size: 0.8rem;
          color: #64748b;
        }

        .approver-ticket-count {
          text-align: center;
          padding: 0.5rem 1rem;
          background: #f1f5f9;
          border-radius: 0.375rem;
        }

        .approver-ticket-count .count {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #3b82f6;
        }

        .approver-ticket-count .label {
          font-size: 0.7rem;
          color: #64748b;
        }

        .recommended-badge {
          font-size: 0.7rem;
          padding: 0.25rem 0.75rem;
          background: #10b981;
          color: white;
          border-radius: 1rem;
          font-weight: 500;
        }

        /* Approver Divider */
        .approver-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 0.5rem 0;
          color: #94a3b8;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .approver-divider::before,
        .approver-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        /* Other Approver Option */
        .other-approver-option .approver-name {
          color: #64748b;
        }

        .other-approver-option.selected .approver-name {
          color: #1e293b;
        }

        /* Other Approver Option - Expanded State */
        .other-approver-option.expanded {
          flex-direction: column;
          align-items: stretch;
        }

        .other-approver-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          cursor: pointer;
        }

        .other-approver-option.expanded .other-approver-header {
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 0.75rem;
        }

        /* Approver Search Container - Integrated inside the box */
        .approver-search-container.integrated {
          margin-left: 0;
          margin-top: 0;
          padding-left: 2.5rem;
        }

        /* Approver Search Container */
        .approver-search-container {
          margin-left: 2.5rem;
          margin-top: 0.5rem;
          position: relative;
        }

        /* Selected Custom Approver Display */
        .selected-custom-approver {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #f0fdf4;
          border: 1px solid #86efac;
          border-radius: 0.5rem;
        }

        .selected-approver-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #16a34a;
        }

        .selected-approver-details {
          display: flex;
          flex-direction: column;
        }

        .selected-approver-name {
          font-weight: 600;
          color: #166534;
        }

        .selected-approver-role {
          font-size: 0.8rem;
          color: #22c55e;
        }

        .change-approver-btn {
          padding: 0.375rem 0.75rem;
          font-size: 0.8rem;
          color: #3b82f6;
          background: white;
          border: 1px solid #3b82f6;
          border-radius: 0.375rem;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .change-approver-btn:hover {
          background: #3b82f6;
          color: white;
        }

        /* Reason for selecting different approver */
        .approver-change-reason {
          margin-top: 1rem;
          padding: 1rem;
          background: #fefce8;
          border: 1px solid #fde047;
          border-radius: 0.5rem;
        }

        .reason-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 500;
          color: #854d0e;
          margin-bottom: 0.5rem;
        }

        .reason-label .optional-text {
          font-weight: 400;
          color: #a16207;
          font-size: 0.8rem;
        }

        .reason-textarea {
          width: 100%;
          padding: 0.75rem;
          font-size: 0.875rem;
          font-family: inherit;
          border: 1px solid #fde047;
          border-radius: 0.375rem;
          background: white;
          resize: vertical;
          min-height: 60px;
          outline: none;
          transition: all 0.15s ease;
        }

        .reason-textarea:focus {
          border-color: #eab308;
          box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.15);
        }

        .reason-textarea::placeholder {
          color: #a16207;
          opacity: 0.7;
        }

        /* Search Input Wrapper */
        .approver-search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .approver-search-input-wrapper .search-icon {
          position: absolute;
          left: 0.875rem;
          color: #94a3b8;
          pointer-events: none;
        }

        .approver-search-input {
          width: 100%;
          padding: 0.75rem 2.5rem;
          font-size: 0.9rem;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          outline: none;
          transition: all 0.15s ease;
        }

        .approver-search-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .approver-search-input::placeholder {
          color: #94a3b8;
        }

        .clear-search-btn {
          position: absolute;
          right: 0.75rem;
          padding: 0.25rem;
          background: #f1f5f9;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.15s ease;
        }

        .clear-search-btn:hover {
          background: #e2e8f0;
          color: #1e293b;
        }

        /* Search Results Dropdown */
        .approver-search-results {
          position: absolute;
          top: calc(100% + 0.5rem);
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
          z-index: 100;
          max-height: 250px;
          overflow-y: auto;
        }

        .search-result-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .search-result-item:hover {
          background: #f8fafc;
        }

        .search-result-item:not(:last-child) {
          border-bottom: 1px solid #f1f5f9;
        }

        .result-avatar {
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e0e7ff;
          border-radius: 50%;
          color: #4f46e5;
        }

        .result-info {
          flex: 1;
        }

        .result-name {
          font-weight: 500;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .result-details {
          font-size: 0.8rem;
          color: #64748b;
        }

        .no-results {
          padding: 2rem;
          text-align: center;
          color: #94a3b8;
        }

        .no-results p {
          margin: 0.5rem 0 0;
          font-size: 0.875rem;
        }

        .search-loading {
          padding: 1.5rem;
          text-align: center;
          color: #64748b;
          font-size: 0.875rem;
        }

        .search-hint {
          padding: 0.75rem;
          text-align: center;
          color: #94a3b8;
          font-size: 0.8rem;
          font-style: italic;
          background: #f8fafc;
          border-radius: 0.375rem;
          margin-top: 0.5rem;
        }

        /* Section Title */
        .section-title {
          margin-bottom: 1rem;
        }

        .section-title h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 0.25rem 0;
        }

        .section-title p {
          font-size: 0.85rem;
          color: #64748b;
          margin: 0;
        }

        /* Filter Bar */
        .filter-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          border: 1px solid #e2e8f0;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .filter-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #334155;
        }

        .filter-info {
          font-size: 0.875rem;
          color: #64748b;
        }

        /* Attachments */
        .existing-attachments {
          background: #f8fafc;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e8f0;
        }

        .existing-attachments h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #334155;
          margin: 0 0 0.75rem 0;
        }

        .attachment-summary {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .ticket-attachments {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0.75rem;
          background: white;
          border-radius: 0.375rem;
          font-size: 0.875rem;
        }

        .ticket-ref {
          font-weight: 500;
          color: #3b82f6;
        }

        .attachment-count {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #64748b;
        }

        .total-attachments {
          padding-top: 0.75rem;
          border-top: 1px solid #e2e8f0;
          font-size: 0.875rem;
          color: #334155;
        }

        .no-items {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }

        .new-attachment-section h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #334155;
          margin: 0 0 1rem 0;
        }

        .attachment-form-grid {
          display: grid;
          grid-template-columns: repeat(9, 1fr);
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .attachments-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .attachment-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 0.375rem;
          border: 1px solid #e2e8f0;
        }

        .attachment-info {
          flex: 1;
        }

        .attachment-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .attachment-title {
          font-weight: 500;
          color: #1e293b;
        }

        .attachment-details {
          font-size: 0.75rem;
          color: #64748b;
          margin-left: 1.25rem;
        }

        .no-attachments {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 0.375rem;
          border: 1px dashed #cbd5e1;
          color: #64748b;
          font-size: 0.875rem;
          text-align: center;
        }

        /* Review Section */
        .review-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .review-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          overflow: hidden;
        }

        .review-card.info {
          background: #f0f9ff;
          border-color: #bae6fd;
        }

        .review-card-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1rem;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #334155;
          margin: 0;
        }

        .review-card.info .review-card-title {
          background: #e0f2fe;
          border-color: #bae6fd;
        }

        .review-content {
          padding: 1rem;
        }

        .review-row {
          display: flex;
          gap: 1rem;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .review-row:last-child {
          border-bottom: none;
        }

        .review-row.full-width {
          flex-direction: column;
          gap: 0.5rem;
        }

        .review-label {
          font-size: 0.8rem;
          color: #64748b;
          min-width: 120px;
        }

        .review-value {
          font-size: 0.875rem;
          color: #1e293b;
          font-weight: 500;
        }

        .review-value.description {
          font-weight: 400;
          line-height: 1.5;
        }

        .review-value.highlight {
          color: #3b82f6;
        }

        .review-tickets-table {
          overflow-x: auto;
        }

        .review-tickets-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .review-tickets-table th {
          padding: 0.75rem;
          text-align: left;
          background: #f8fafc;
          font-weight: 600;
          color: #475569;
          border-bottom: 2px solid #e2e8f0;
        }

        .review-tickets-table td {
          padding: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .review-tickets-table tfoot td {
          background: #f8fafc;
          border-top: 2px solid #e2e8f0;
        }

        .approver-display {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .approver-avatar {
          width: 48px;
          height: 48px;
          background: var(--primary, #3b82f6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .approver-details {
          display: flex;
          flex-direction: column;
        }

        .approver-details .approver-name {
          font-size: 1rem;
        }

        .approver-details .approver-role {
          font-size: 0.85rem;
        }

        .not-selected {
          color: #ef4444;
          font-style: italic;
        }

        .not-selected-container {
          display: flex;
          align-items: center;
        }

        /* Changed badge for Final Approver header */
        .changed-badge {
          font-size: 0.7rem;
          padding: 0.2rem 0.5rem;
          background: #fef3c7;
          color: #92400e;
          border-radius: 0.25rem;
          font-weight: 500;
          margin-left: 0.5rem;
        }

        /* Consolidation Summary Styles */
        .consolidation-summary {
          margin-top: 1rem;
          padding: 1rem;
          background: #fefce8;
          border: 1px solid #fde047;
          border-radius: 0.5rem;
        }

        .consolidation-summary .summary-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 600;
          color: #854d0e;
          margin: 0 0 0.75rem 0;
        }

        .consolidation-summary .summary-label {
          font-size: 0.8rem;
          color: #92400e;
          font-weight: 500;
          margin-bottom: 0.375rem;
          display: block;
        }

        .consolidation-summary .original-approvers {
          margin-bottom: 0.75rem;
        }

        .consolidation-summary .approver-list {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .consolidation-summary .original-approver-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: #78350f;
          padding: 0.25rem 0.5rem;
          background: white;
          border-radius: 0.25rem;
        }

        .consolidation-summary .original-approver-item .ticket-count {
          color: #a16207;
          font-size: 0.75rem;
        }

        .consolidation-summary .change-reason {
          padding-top: 0.75rem;
          border-top: 1px dashed #fcd34d;
        }

        .consolidation-summary .reason-text {
          font-size: 0.85rem;
          color: #78350f;
          margin: 0;
          padding: 0.5rem;
          background: white;
          border-radius: 0.25rem;
          line-height: 1.4;
        }

        .consolidation-summary .change-reason-warning {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #fff7ed;
          border: 1px solid #fdba74;
          border-radius: 0.375rem;
          font-size: 0.8rem;
          color: #c2410c;
          margin-top: 0.75rem;
        }

        .consolidation-summary .add-reason-btn {
          margin-left: auto;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          color: #ea580c;
          background: white;
          border: 1px solid #ea580c;
          border-radius: 0.25rem;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .consolidation-summary .add-reason-btn:hover {
          background: #ea580c;
          color: white;
        }

        .validation-errors {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 0.5rem;
          padding: 1rem;
        }

        .validation-errors h4 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #991b1b;
          margin: 0 0 0.75rem 0;
        }

        .validation-errors ul {
          margin: 0;
          padding-left: 1.5rem;
          color: #b91c1c;
          font-size: 0.875rem;
        }

        .validation-errors li {
          margin-bottom: 0.25rem;
        }

        /* Footer Summary */
        .footer-summary {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #64748b;
        }

        .footer-summary .summary-item.highlight {
          color: #3b82f6;
        }

        .footer-summary .summary-divider {
          color: #cbd5e1;
        }

        .footer-actions {
          display: flex;
          gap: 0.75rem;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .expanded-grid {
            grid-template-columns: 1fr;
          }

          .filter-bar {
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
          }

          .attachment-form-grid {
            grid-template-columns: 1fr;
          }

          .attachment-form-grid > div {
            grid-column: span 1 !important;
          }

          .tab-button span:not(.error-indicator):not(.optional-label) {
            display: none;
          }

          .tab-button {
            padding: 0.75rem;
          }
        }

        /* Attachment Actions */
        .attachment-actions {
          display: flex;
          gap: 0.25rem;
        }

        .btn-danger {
          color: #ef4444;
        }

        .btn-danger:hover {
          background: #fef2f2;
        }

        /* Preview Modal - higher z-index to appear above main modal */
        .preview-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .preview-modal-content {
          background: white;
          border-radius: 0.5rem;
          min-width: 400px;
          min-height: 300px;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .preview-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .preview-modal-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .preview-modal-body {
          padding: 1rem;
          overflow: auto;
          flex: 1;
        }

        .preview-modal-body img {
          max-width: 100%;
          height: auto;
        }

        .preview-modal-body iframe {
          width: 100%;
          min-width: 600px;
          height: 70vh;
          border: none;
        }

        /* Confirmation Dialog Styles */
        .confirm-dialog-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 10001;
        }

        .confirm-dialog {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          z-index: 10002;
          width: 90%;
          max-width: 500px;
          overflow: hidden;
        }

        .confirm-dialog-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.25rem 1.5rem;
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-bottom: 1px solid #f59e0b;
        }

        .confirm-dialog-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #92400e;
        }

        .confirm-dialog-content {
          padding: 1.5rem;
        }

        .confirm-dialog-content > p {
          margin: 0 0 1rem 0;
          color: #475569;
          font-size: 1rem;
        }

        .confirm-dialog-summary {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .summary-row:last-child {
          border-bottom: none;
        }

        .summary-label {
          font-weight: 500;
          color: #64748b;
        }

        .summary-value {
          font-weight: 600;
          color: #1e293b;
          text-align: right;
          max-width: 60%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .confirm-dialog-note {
          font-size: 0.875rem;
          color: #64748b;
          font-style: italic;
          margin: 0;
        }

        .confirm-dialog-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
        }

        /* Result Alert Styles */
        .result-alert-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 10003;
        }

        .result-alert {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          z-index: 10004;
          width: 90%;
          max-width: 400px;
          padding: 2rem;
          text-align: center;
        }

        .result-alert-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .result-alert-success .result-alert-icon {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #059669;
        }

        .result-alert-error .result-alert-icon {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
        }

        .result-alert-content h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .result-alert-success .result-alert-content h3 {
          color: #059669;
        }

        .result-alert-error .result-alert-content h3 {
          color: #dc2626;
        }

        .result-alert-content p {
          margin: 0 0 1.5rem 0;
          color: #64748b;
          font-size: 1rem;
          line-height: 1.5;
        }

        .result-alert .btn {
          min-width: 120px;
        }
      `}</style>

      {/* Preview Modal - Full-screen document viewer via Portal */}
      {previewAttachment && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '1rem',
          }}
          onClick={handleClosePreview}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              width: '95vw',
              height: '95vh',
              maxWidth: '1400px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <IconSystem.file size={20} style={{ color: '#3b82f6' }} />
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1e293b' }}>
                  {previewAttachment.fileName}
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <a
                  href={previewAttachment.url}
                  download={previewAttachment.fileName}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '0.375rem',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconSystem.download size={16} />
                  Download
                </a>
                <button
                  onClick={handleClosePreview}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                  title="Close preview"
                >
                  <IconSystem.x size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                padding: '1rem',
              }}
            >
              {previewAttachment.fileType.startsWith('image/') ? (
                <img
                  src={previewAttachment.url}
                  alt={previewAttachment.fileName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
              ) : previewAttachment.fileType === 'application/pdf' ? (
                <iframe
                  src={previewAttachment.url}
                  title={previewAttachment.fileName}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '0.5rem',
                    background: 'white',
                  }}
                />
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '3rem',
                    background: 'white',
                    borderRadius: '0.75rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <IconSystem.file size={64} style={{ opacity: 0.4, marginBottom: '1.5rem', color: '#64748b' }} />
                  <p style={{ fontSize: '1.125rem', fontWeight: 500, color: '#1e293b', marginBottom: '0.5rem' }}>
                    Preview not available
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    This file type ({previewAttachment.fileType}) cannot be previewed in the browser.
                  </p>
                  <a
                    href={previewAttachment.url}
                    download={previewAttachment.fileName}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: '#3b82f6',
                      color: 'white',
                      borderRadius: '0.5rem',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    <IconSystem.download size={18} />
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ConsolidatedRequestModal;
